import { FieldArray, getIn, useFormikContext } from "formik"
import { useRef } from "react"
import { v4 as uuidV4 } from "uuid"
import logger from "../../../../../Logger"
import { csvFileToArray, parseFileFormData } from "../../import-utils"
import { FIELD_TYPES, tableGroupValueIdKey } from "../../dynamic-form.constants"
import { MultiSelectDropdownControl } from "../multi-select-dropdown"
import { SUB_TEAM_FORM_FIELDS, seatsForRosterRole, teamForRole } from "../../../../../data-utils/subTeams"

// Intra-cell separator for a MULTI_DROPDOWN cell in CSV (a comma would collide with the CSV
// delimiter). Export joins the picked values with it; import splits on it OR a semicolon. CAVEAT:
// a scenario role NAME that itself contains "|" or ";" is mis-split on import — the fragments match
// no option and are KEPT as unknown tokens (visible in the picker summary, REJECTED by the server's
// role validation on submit), so a corrupted import is surfaced, never silently mis-assigned.
// Role names with these characters are unusual; avoid them in scenario authoring.
const MULTI_CELL_SEP = " | "

const pageLogger = logger.child({ module: "Dynamic Form | Roster Table" })

// Map a free-form CSV Language cell to a supported code, so imports accept the human
// label ("French", "Suomi") or the code ("fr", "FR") interchangeably. Anything
// unrecognized (including blank) resolves to "" — i.e. inherit the exercise default —
// so a stray value never wedges the select on a non-option. Mirrors the client
// allowlist en/fr/fi (see gameSessionUtils SUPPORTED_LANGUAGES).
const LANGUAGE_IMPORT_ALIASES = {
  en: "en",
  english: "en",
  fr: "fr",
  french: "fr",
  français: "fr",
  francais: "fr",
  fi: "fi",
  finnish: "fi",
  suomi: "fi",
}
const normalizeImportedLanguage = (raw) => LANGUAGE_IMPORT_ALIASES[`${raw ?? ""}`.trim().toLowerCase()] ?? ""

// Compare a CSV-imported dropdown value against an option value symmetrically with what the CSV
// EXPORT does to field contents (handleDownloadCsv replaces commas with spaces). Without this, a
// comma-bearing option (a scenario role like "Incident Response, Lead") is exported as
// "Incident Response  Lead" and would no longer strict-equal its option on re-import, so the Role
// would silently reset to "". Applying the same comma→space + whitespace-collapse to BOTH sides lets
// a just-downloaded roster round-trip; the caller stores the canonical option value, not this form.
const normalizeOptionForMatch = (value) => `${value ?? ""}`.replace(/,/g, " ").replace(/\s+/g, " ").trim().toLowerCase()

// Person / Bot glyphs for the "Played by" switch (PERSON_BOT_SWITCH). Icons, not words —
// the switch state IS the label (off = person, on = bot).
const PersonIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <circle cx="12" cy="8" r="3.2" />
    <path d="M5.5 19a6.5 6.5 0 0 1 13 0" />
  </svg>
)
const BotIcon = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <rect x="4.5" y="7.5" width="15" height="11" rx="2.5" />
    <path d="M12 7.5V4.5M9.5 12h.01M14.5 12h.01M9 16h6" />
  </svg>
)

// Bespoke, controlled roster table used ONLY by the Step-2 details form for the
// group/multiplayer "Players" and "Observers" rosters. Renders the flattened
// mock's markup (mock-bottom.html): a bordered container with an uppercase faint
// header row and one-or-more editable rows, "+ Add" right-justified per the last
// row, a small remove (×) per row, and "Import CSV / Download CSV" gold text
// links pinned top-right of the section heading. Wires to the SAME Formik values
// as the FieldTable it replaces (identical row shapes) so the launch payload is
// unchanged. Deliberately does NOT touch the shared FieldTable/editToolbar.

// Columns rendered in the table (exclude hidden/action fields — width 0, enabled
// false, action buttons). The Facilitator toggle keeps its column even though it
// is not a text input.
const visibleFields = (fieldData) => (fieldData ?? []).filter((f) => f.type !== FIELD_TYPES.ACTION_BTN && f.enabled !== false && f.width !== 0)

// A blank row seeded from the group field schema (every visible + hidden field
// initialised to its default, plus a fresh __id so React keys stay stable).
const blankRow = (fieldData) => {
  const row = {}
  ;(fieldData ?? [])
    .filter((f) => f.type !== FIELD_TYPES.ACTION_BTN)
    .forEach((f) => {
      row[f.name] =
        f.type === FIELD_TYPES.FACILITATOR_TOGGLE
          ? false
          : f.type === FIELD_TYPES.MULTI_DROPDOWN
            ? Array.isArray(f.value)
              ? [...f.value]
              : []
            : (f.value ?? (f.toggleValues ? f.toggleValues.off : ""))
    })
  row[tableGroupValueIdKey] = uuidV4()
  return row
}

function RosterTable(props) {
  const { id, name, fieldData, groupLabel, isExportEnabled, isImportEnabled } = props
  const { values, errors, touched, submitCount, setFieldValue } = useFormikContext()
  const fileInputRef = useRef(null)

  const cols = visibleFields(fieldData)
  const rows = getIn(values, name) ?? []

  // A roster with a person/bot switch must always keep at least one human seat. Count the
  // human rows so the switch can lock the LAST remaining human (can't flip everyone to bots).
  const switchCol = cols.find((f) => f.type === FIELD_TYPES.PERSON_BOT_SWITCH)
  const humanSeatCount = switchCol ? rows.filter((r) => (r[switchCol.name] ?? "human") === "human").length : rows.length

  // Grid template: text columns share the flexible space; the Role select and the
  // Facilitator / Enable-Chat toggle take fixed widths; a trailing 64px column
  // holds the "+ Add" / remove affordance. Mirrors the mock's .g-players /.g-obs.
  // A DROPDOWN flagged with `toggleValues` (e.g. Observers' Enable-Chat Yes/No)
  // renders as a single radio circle like the Facilitator toggle, but stores the
  // on/off string so the launch payload shape (canChat: "Yes"/"No") is unchanged.
  const isToggleField = (f) => f.type === FIELD_TYPES.FACILITATOR_TOGGLE || (f.type === FIELD_TYPES.DROPDOWN && f.toggleValues)

  const gridTemplate = cols
    .map((f) => {
      if (isToggleField(f)) return "90px"
      if (f.type === FIELD_TYPES.PERSON_BOT_SWITCH) return "84px" // just the switch; bot picker lives in the Name cell
      if (f.type === FIELD_TYPES.DROPDOWN) return "150px"
      if (f.name === "email") return "1.3fr"
      return "1fr"
    })
    .concat(["64px"])
    .join(" ")

  const fieldError = (rowIndex, fieldName) => {
    const rowErr = getIn(errors, `${name}[${rowIndex}]`)
    const rowTouched = getIn(touched, `${name}[${rowIndex}]`)
    const show = submitCount > 0 || (rowTouched && getIn(rowTouched, fieldName))
    return show ? getIn(rowErr, fieldName) : ""
  }

  const groupError = getIn(errors, name)
  const groupErrorTouched = !!getIn(touched, name) || submitCount > 0
  const groupErrorMessage = groupErrorTouched && typeof groupError === "string" ? groupError : ""

  // Facilitators are CO-EQUAL: any number of players can be marked (session.facilitators[]).
  // Toggling one row flips only that row and leaves the others untouched (checkbox semantics),
  // unlike the original single-select FACILITATOR_TOGGLE which forced every other row to false.
  const setFacilitator = (rowId) => {
    const updated = rows.map((row) => (row[tableGroupValueIdKey] === rowId ? { ...row, isFacilitator: !row.isFacilitator } : row))
    setFieldValue(name, updated)
  }

  const handleDownloadCsv = () => {
    const exportCols = cols.filter((f) => f.type !== FIELD_TYPES.FACILITATOR_TOGGLE)
    const header = exportCols.map((f) => f.label)
    // Facilitator / chat toggles export as their label too so a re-import round-trips.
    const toggleCols = (fieldData ?? []).filter((f) => f.type === FIELD_TYPES.FACILITATOR_TOGGLE)
    const headerRow = header.concat(toggleCols.map((f) => f.label)).join(",")
    const bodyRows = rows.map((row) =>
      exportCols
        .map((f) => {
          if (f.type === FIELD_TYPES.MULTI_DROPDOWN) {
            // Serialize the picked values with a non-comma separator so the CSV columns stay intact
            // ([] == "All" exports as an empty cell, which re-imports back to All). A LEGACY scalar
            // string (pre-multi-select saved roster) is exported as itself — treating it as [] would
            // silently erase the selected role on a download/re-import round-trip.
            const arr = Array.isArray(row[f.name]) ? row[f.name] : row[f.name] ? [row[f.name]] : []
            return arr.join(MULTI_CELL_SEP).replace(/,/g, " ")
          }
          return `${row[f.name] ?? ""}`.replace(/,/g, " ")
        })
        .concat(toggleCols.map((f) => (row[f.name] === true ? "Yes" : "No")))
        .join(",")
    )
    const csv = [headerRow, ...bodyRows].join("\n")
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `${(groupLabel || name).toLowerCase().replace(/\s+/g, "-")}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  }

  const handleImportChange = async (event, arrayHelpers) => {
    const file = event.target.files?.[0]
    event.target.value = "" // allow re-importing the same file
    if (!file) return
    try {
      const parsed = await parseFileFormData(file)
      const importRows = csvFileToArray(parsed.utf8 ?? "")
      const allFields = (fieldData ?? []).filter((f) => f.type !== FIELD_TYPES.ACTION_BTN)
      const appended = []
      importRows.forEach((importItem) => {
        const mapped = blankRow(fieldData)
        let matchedAny = false
        for (const f of allFields) {
          for (const [key, value] of Object.entries(importItem)) {
            const trimmedKey = `${key}`.trim()
            if (trimmedKey === f.label || trimmedKey === f.name) {
              const raw = `${value ?? ""}`.trim()
              if (f.type === FIELD_TYPES.FACILITATOR_TOGGLE) {
                mapped[f.name] = /^(yes|true|1)$/i.test(raw)
              } else if (f.name === "language") {
                // Accept code or human label; unknown ⇒ "" (inherit exercise default).
                mapped[f.name] = normalizeImportedLanguage(raw)
              } else if (f.type === FIELD_TYPES.DROPDOWN && Array.isArray(f.options)) {
                // Constrain an imported value to the dropdown's real options — an unknown value (e.g.
                // a Role that isn't in this scenario) becomes "" so the field's REQUIRED check flags it,
                // rather than silently accepting an invalid selection. Two passes, and the looser one is
                // used ONLY when it maps to exactly ONE option so the importer never guesses between
                // siblings:
                // 1) STRICT case-sensitive exact — always unambiguous (an exact value match).
                // 2) NORMALIZED fallback (lowercased, commas→space, whitespace collapsed) — covers a
                //    case difference AND a just-downloaded roster round-trip (CSV export replaces commas
                //    with spaces, so "Incident Response, Lead" no longer equals its option). Used only
                //    when it matches exactly ONE option: two options that collapse to the same key —
                //    whether by case ("Admin"/"admin") or comma/space ("A,B"/"A B") — are ambiguous, so
                //    leave "" and let REQUIRED flag it.
                const rawTrim = raw.trim()
                let matchedOption = f.options.find((o) => `${o.value ?? ""}`.trim() === rawTrim) ?? null
                if (!matchedOption) {
                  const normRaw = normalizeOptionForMatch(raw)
                  const normMatches = f.options.filter((o) => normalizeOptionForMatch(o.value) === normRaw)
                  matchedOption = normMatches.length === 1 ? normMatches[0] : null
                }
                mapped[f.name] = matchedOption ? matchedOption.value : ""
              } else if (f.type === FIELD_TYPES.MULTI_DROPDOWN && Array.isArray(f.options)) {
                // Multi-select cell: split on the export separator (or a bare ';') and match each token
                // to a real option (strict, then unambiguous-normalized — same rule as the single
                // DROPDOWN). A matched token canonicalizes to the option value; an UNKNOWN token is kept
                // verbatim (NOT dropped) so it stays visible in the picker summary and is REJECTED by the
                // server's role validation on submit — a typo must never silently widen the row to "All".
                // An empty cell stays [] (== "All roles").
                const tokens = raw
                  .split(/[|;]/)
                  .map((t) => t.trim())
                  .filter(Boolean)
                const picked = []
                for (const tok of tokens) {
                  let opt = f.options.find((o) => `${o.value ?? ""}`.trim() === tok) ?? null
                  if (!opt) {
                    const normTok = normalizeOptionForMatch(tok)
                    const normMatches = f.options.filter((o) => normalizeOptionForMatch(o.value) === normTok)
                    opt = normMatches.length === 1 ? normMatches[0] : null
                  }
                  const val = opt ? opt.value : tok
                  if (!picked.includes(val)) picked.push(val)
                }
                mapped[f.name] = picked
              } else {
                mapped[f.name] = raw
              }
              if (raw) matchedAny = true
              break
            }
          }
        }
        if (matchedAny) appended.push(mapped)
      })
      if (appended.length === 0) {
        pageLogger.warn({ name }, "CSV import produced no valid rows")
        return
      }
      // CSV import REPLACES the roster — the table shows EXACTLY the imported rows, dropping the
      // blank default seed row AND any pre-filled/scenario-role defaults (user direction 2026-09-10:
      // "the default is removed and only what is in the csv is shown"). Import = load this file.
      setFieldValue(name, appended)
      pageLogger.info({ name, count: appended.length }, "CSV import replaced roster")
    } catch (error) {
      pageLogger.error({ error }, "CSV import failed")
    }
  }

  return (
    <FieldArray
      id={id}
      name={name}
      render={(arrayHelpers) => (
        <div className="col-12 ctd-roster">
          <div className="ctd-roster-toolbar">
            {isImportEnabled ? (
              <>
                <button type="button" className="ctd-roster-link" onClick={() => fileInputRef.current?.click()}>
                  Import CSV
                </button>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,.txt,text/csv"
                  style={{ display: "none" }}
                  onChange={(event) => handleImportChange(event, arrayHelpers)}
                />
              </>
            ) : null}
            {isExportEnabled ? (
              <button type="button" className="ctd-roster-link" onClick={handleDownloadCsv}>
                Download CSV
              </button>
            ) : null}
          </div>

          <div className="ctd-roster-tbl">
            <div className="ctd-roster-head" style={{ gridTemplateColumns: gridTemplate }}>
              {cols.map((f) => (
                <span key={`${id}-h-${f.name}`}>{f.label}</span>
              ))}
              <span></span>
            </div>

            {rows.map((row, rowIndex) => {
              const rowId = row[tableGroupValueIdKey] ?? rowIndex
              const isLastRow = rowIndex === rows.length - 1
              // A seat played by a bot (PERSON_BOT_SWITCH value !== "human") gets a faint rail,
              // and the bot picker is hosted in the Name cell (botPickerHost) so the switch column
              // stays narrow. Resolve the switch field + its path once for the whole row.
              const switchField = cols.find((f) => f.type === FIELD_TYPES.PERSON_BOT_SWITCH)
              const switchVal = switchField ? (row[switchField.name] ?? "human") : "human"
              const rowIsBot = !!switchVal && switchVal !== "human"
              const switchPath = switchField ? `${name}[${rowIndex}].${switchField.name}` : null
              // Selecting a bot also auto-fills the seat's Role from that bot (switchField.roleField).
              const rolePath = switchField?.roleField ? `${name}[${rowIndex}].${switchField.roleField}` : null
              const roleForBot = (key) => (switchField?.options ?? []).find((o) => o.value === key)?.role ?? ""
              // The role field may be the legacy STRING or the new MULTI_DROPDOWN array — write the
              // matching shape so a human↔bot toggle never leaves a stale scalar in an array field.
              const roleFieldIsArray = cols.some((f) => f.name === switchField?.roleField && f.type === FIELD_TYPES.MULTI_DROPDOWN)
              const applyPlayedBy = (key) => {
                if (switchPath) setFieldValue(switchPath, key)
                if (rolePath) {
                  if (key === "human") {
                    // Back to human: clear to the empty selection (the person re-picks their seats)
                    setFieldValue(rolePath, roleFieldIsArray ? [] : "")
                  } else {
                    const botRole = roleForBot(key)
                    setFieldValue(rolePath, roleFieldIsArray ? (botRole ? [botRole] : []) : botRole)
                  }
                }
              }
              return (
                <div className={`ctd-roster-row${rowIsBot ? " is-bot" : ""}`} key={`${id}-r-${rowId}`} style={{ gridTemplateColumns: gridTemplate }}>
                  {cols.map((f) => {
                    const cellPath = `${name}[${rowIndex}].${f.name}`
                    const err = fieldError(rowIndex, f.name)
                    // A field gated by `activeWhen` (e.g. Name/Email on a bot row) is not
                    // applicable this row. The designated host (Name, botPickerHost) shows the
                    // bot picker instead; the rest show a disabled "—" so the grid stays aligned.
                    if (f.activeWhen && row[f.activeWhen.field] !== f.activeWhen.is) {
                      if (f.botPickerHost && rowIsBot && switchField && (switchField.options ?? []).length) {
                        return (
                          <select
                            key={`${id}-c-${rowId}-${f.name}`}
                            className="ctd-roster-sel"
                            value={switchVal}
                            aria-label="Choose bot"
                            onChange={(event) => applyPlayedBy(event.target.value)}
                          >
                            {(switchField.options ?? []).map((opt) => (
                              <option key={`${opt.value}`} value={opt.value}>
                                {opt.display ?? opt.value}
                              </option>
                            ))}
                          </select>
                        )
                      }
                      return <input key={`${id}-c-${rowId}-${f.name}`} className="ctd-roster-inp" placeholder="—" value="" disabled readOnly />
                    }
                    if (f.type === FIELD_TYPES.FACILITATOR_TOGGLE) {
                      const checked = !!row[f.name]
                      return (
                        <button
                          type="button"
                          key={`${id}-c-${rowId}-${f.name}`}
                          className={`ctd-roster-radio${checked ? " is-on" : ""}`}
                          aria-pressed={checked}
                          aria-label={f.label}
                          onClick={() => setFacilitator(rowId)}
                        ></button>
                      )
                    }
                    if (f.type === FIELD_TYPES.PERSON_BOT_SWITCH) {
                      // "Played by": person⇄bot icon switch only (narrow column). Off ⇒ "human";
                      // on ⇒ this seat is a bot (default: first option) and the bot picker appears
                      // in the Name cell (botPickerHost). Stored value: "human" | <botKey>.
                      const opts = f.options ?? []
                      const isBot = !!(row[f.name] ?? "human") && (row[f.name] ?? "human") !== "human"
                      const firstBot = opts[0]?.value
                      // Keep at least one human: the last remaining human seat can't be flipped
                      // to a bot. bot→person is always allowed.
                      const isLastHuman = !isBot && humanSeatCount <= 1
                      const locked = !opts.length || isLastHuman
                      // Flip person⇄bot; applyPlayedBy also auto-fills / clears the Role cell.
                      const toggle = () => {
                        if (locked) return
                        applyPlayedBy(isBot ? "human" : (firstBot ?? "human"))
                      }
                      return (
                        <button
                          type="button"
                          key={`${id}-c-${rowId}-${f.name}`}
                          role="switch"
                          aria-checked={isBot}
                          aria-label="Played by a person or a bot"
                          title={
                            isLastHuman ? "At least one player must be a person" : isBot ? "Bot — click for a person" : "Person — click for a bot"
                          }
                          className={`ctd-roster-switch${isBot ? " is-bot" : ""}`}
                          onClick={toggle}
                          disabled={locked}
                        >
                          <span className="ctd-roster-knob">
                            <span className="ctd-icn ctd-icn-person">
                              <PersonIcon />
                            </span>
                            <span className="ctd-icn ctd-icn-bot">
                              <BotIcon />
                            </span>
                          </span>
                        </button>
                      )
                    }
                    if (f.type === FIELD_TYPES.DROPDOWN && f.toggleValues) {
                      // Yes/No-style toggle (Observers' Enable Chat) — a radio circle
                      // that flips between the on/off strings. Not mutually exclusive.
                      const { on, off } = f.toggleValues
                      const checked = row[f.name] === on
                      return (
                        <button
                          type="button"
                          key={`${id}-c-${rowId}-${f.name}`}
                          className={`ctd-roster-radio${checked ? " is-on" : ""}`}
                          aria-pressed={checked}
                          aria-label={f.label}
                          onClick={() => setFieldValue(cellPath, checked ? off : on)}
                        ></button>
                      )
                    }
                    if (f.type === FIELD_TYPES.MULTI_DROPDOWN) {
                      // Multi-select role picker in a roster cell. Value is a string[] ([] == "All").
                      const cellVal = Array.isArray(row[f.name]) ? row[f.name] : row[f.name] ? [row[f.name]] : []
                      return (
                        <MultiSelectDropdownControl
                          key={`${id}-c-${rowId}-${f.name}`}
                          className={`ctd-roster-msdd${err ? " is-invalid" : ""}`}
                          // Row-distinct accessible name: the roster is a div grid (no row/header
                          // semantics), so bake the row's identity (name/email, else row number) in.
                          ariaLabel={`${f.label || f.name} for ${row.name || row.email || `row ${rowIndex + 1}`}`}
                          value={cellVal}
                          onChange={(next) => setFieldValue(cellPath, next)}
                          options={f.options ?? []}
                          allLabel={f.allLabel ?? "All"}
                          singleSelect={f.singleSelect === true}
                        />
                      )
                    }
                    if (f.type === FIELD_TYPES.DROPDOWN) {
                      return (
                        <select
                          key={`${id}-c-${rowId}-${f.name}`}
                          className={`ctd-roster-sel${err ? " is-invalid" : ""}`}
                          value={row[f.name] ?? ""}
                          onChange={(event) => setFieldValue(cellPath, event.target.value)}
                        >
                          {(f.options ?? []).map((opt) => (
                            <option key={`${opt.value}`} value={opt.value}>
                              {opt.display ?? opt.value}
                            </option>
                          ))}
                        </select>
                      )
                    }
                    // Enable-Chat is modelled as a Yes/No dropdown in field config; the
                    // block above handles it. All remaining columns are text inputs.
                    const textInput = (
                      <input
                        key={`${id}-c-${rowId}-${f.name}`}
                        className={`ctd-roster-inp${err ? " is-invalid" : ""}`}
                        type={f.type === FIELD_TYPES.EMAIL ? "email" : "text"}
                        placeholder={f.placeholder || f.label}
                        value={row[f.name] ?? ""}
                        title={err || undefined}
                        onChange={(event) => setFieldValue(cellPath, event.target.value)}
                      />
                    )
                    // Collaborative sub-teams: a read-only tag naming the sub-team(s) this row's role lands in.
                    const subTeams = values?.[SUB_TEAM_FORM_FIELDS.SUB_TEAMS]
                    if (f.subTeamSeats && Array.isArray(subTeams) && subTeams.length && values?.[SUB_TEAM_FORM_FIELDS.PLAYERS_SEE_EACH_OTHER] !== false) {
                      const seats = seatsForRosterRole({ roleValue: row[f.name], scenarioRoles: f.subTeamSeats })
                      const names = [...new Set(seats.map((s) => teamForRole(subTeams, s)?.name).filter(Boolean))]
                      return (
                        <div key={`${id}-c-${rowId}-${f.name}`} className="ctd-roster-rolecell">
                          {textInput}
                          <span className={`ctd-roster-subteam${names.length ? "" : " is-shared"}`}>{names.length ? names.join(" + ") : "Shared"}</span>
                        </div>
                      )
                    }
                    return textInput
                  })}
                  <div className="ctd-roster-rowactions">
                    {rows.length > 1 ? (
                      <button type="button" className="ctd-roster-remove" aria-label="Remove row" onClick={() => arrayHelpers.remove(rowIndex)}>
                        ×
                      </button>
                    ) : null}
                    {isLastRow ? (
                      <button type="button" className="ctd-roster-add" onClick={() => arrayHelpers.push(blankRow(fieldData))}>
                        + Add
                      </button>
                    ) : null}
                  </div>
                </div>
              )
            })}
          </div>
          {groupErrorMessage ? <div className="ctd-roster-error">{groupErrorMessage}</div> : null}
        </div>
      )}
    />
  )
}

export default RosterTable
